import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Store, ShieldCheck, Sparkles } from 'lucide-react';

const badges = [
  {
    icon: Truck,
    title: 'Envío Express',
    subtitle: 'Rápido y seguro',
    description: 'Envío gratis a todo México desde $1,197. Debajo de ese monto, tarifa fija de $200.',
    accent: 'cobalt'
  },
  {
    icon: Store,
    title: 'Paga en OXXO',
    subtitle: 'Sin tarjeta necesaria',
    description: 'Genera tu ficha de pago y liquida en cualquier OXXO. Tu pedido se procesa al confirmar el pago.',
    accent: 'petroleum'
  },
  {
    icon: ShieldCheck,
    title: 'Pago 100% Seguro',
    subtitle: 'Protegido por Stripe',
    description: 'Tus datos están encriptados. Aceptamos todas las tarjetas de crédito y débito.',
    accent: 'cobalt'
  },
  {
    icon: Sparkles,
    title: 'Garantía 15 Días',
    subtitle: 'Tu confianza es primero',
    description: 'Si no te enamoras de tu fragancia, te ayudamos con cambio o devolución.',
    accent: 'petroleum'
  }
];

// Estilos completos (no interpolados) por acento, para que Tailwind los detecte de forma confiable.
const ACCENT_STYLES = {
  cobalt: {
    border: 'hover:border-brand-cobalt/50',
    shadow: 'hover:shadow-brand-cobalt/10',
    iconBg: 'bg-brand-cobalt/10 group-hover:bg-brand-cobalt/20',
    icon: 'text-brand-cobalt',
    subtitle: 'text-brand-cobalt/80',
    glow: 'bg-gradient-to-br from-brand-cobalt/5 to-transparent',
  },
  petroleum: {
    border: 'hover:border-brand-petroleum/50',
    shadow: 'hover:shadow-brand-petroleum/10',
    iconBg: 'bg-brand-petroleum/10 group-hover:bg-brand-petroleum/20',
    icon: 'text-brand-petroleum',
    subtitle: 'text-brand-petroleum/80',
    glow: 'bg-gradient-to-br from-brand-petroleum/5 to-transparent',
  },
};

const TrustBadges = () => {
  return (
    <section className="py-4 md:py-6 px-4 md:px-8 bg-brand-bone">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {badges.map((badge, index) => {
            const Icon = badge.icon;
            const accent = ACCENT_STYLES[badge.accent];

            return (
              <motion.div
                key={badge.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className={`
                  relative group p-4 md:p-5 rounded-xl
                  bg-white
                  border border-brand-black/10
                  ${accent.border}
                  transition-all duration-300
                  hover:shadow-lg ${accent.shadow}
                `}
              >
                {/* Icon Container */}
                <div className={`
                  w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 rounded-full
                  flex items-center justify-center
                  ${accent.iconBg}
                  transition-colors duration-300
                `}>
                  <Icon
                    size={20}
                    className={`
                      md:w-6 md:h-6
                      ${accent.icon}
                      group-hover:scale-110 transition-transform duration-300
                    `}
                  />
                </div>

                {/* Title */}
                <h3 className="text-brand-black font-bold text-sm md:text-base mb-0.5 md:mb-1">
                  {badge.title}
                </h3>

                {/* Subtitle */}
                <p className={`text-xs md:text-sm font-medium mb-1.5 md:mb-2 ${accent.subtitle}`}>
                  {badge.subtitle}
                </p>

                {/* Description */}
                <p className="text-brand-black/50 text-[10px] md:text-xs leading-relaxed">
                  {badge.description}
                </p>

                {/* Decorative glow on hover */}
                <div className={`
                  absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
                  transition-opacity duration-300 pointer-events-none
                  ${accent.glow}
                `} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TrustBadges;
